import { useMemo, useState } from 'react';
import { Sparkles, FlaskConical, ExternalLink, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useEmbeddedDemoData } from '@/hooks/useEmbeddedDemoData';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import { useOpenPortal } from '@/hooks/useOpenPortal';
import { CONTROL_MIN_HEIGHT } from '@/components/ui/control-size';
import { cn } from '@/lib/utils';

export type NarrativeState = 'welcome' | 'demo' | 'none';

/** Pure show-logic — unit-tested without rendering. */
export function narrativeState(input: {
  hasDemoData: boolean;
  dismissed: boolean;
}): NarrativeState {
  if (!input.hasDemoData) return 'none';
  return input.dismissed ? 'demo' : 'welcome';
}

function dismissKey(orgId: string | null): string {
  return `aelo:embedded:welcome-dismissed:${orgId ?? 'anon'}`;
}

export function EmbeddedNarrative() {
  const { t } = useTranslation('dashboard');
  const { hasDemoData } = useEmbeddedDemoData();
  const { orgId } = useEmbeddedOrg();
  const { openPortal, isOpening } = useOpenPortal();

  // orgId starts null and resolves async (EmbeddedOrgContext fetches the session),
  // so a lazy useState init would lock onto the wrong ':anon' key forever and the
  // dismiss would never persist. Re-derive from storage whenever orgId changes,
  // and track this-session dismissals separately so the click hides immediately.
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const persistedDismissed = useMemo(() => {
    try {
      return localStorage.getItem(dismissKey(orgId)) === '1';
    } catch {
      return false;
    }
  }, [orgId]);
  const dismissed = sessionDismissed || persistedDismissed;

  const state = narrativeState({ hasDemoData, dismissed });
  if (state === 'none') return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissKey(orgId), '1');
    } catch {
      /* storage blocked in iframe — fall through, still hide via state */
    }
    setSessionDismissed(true);
  };

  if (state === 'welcome') {
    return (
      <div className="mb-4 rounded-card border border-border bg-bg-elevated p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-dim">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-fg">
              {t('embedded.welcomeTitle')}
            </h3>
            <p className="mt-1 text-sm text-fg-muted">{t('embedded.welcomeBody')}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="md"
                onClick={() => void openPortal()}
                disabled={isOpening}
                className="gap-2"
              >
                {isOpening ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4" />
                )}
                {t('embedded.openPortal')}
              </Button>
              <Button variant="outline" size="md" onClick={handleDismiss}>
                {t('embedded.dismiss')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // state === 'demo'
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-card border border-warning bg-warning-dim px-3 py-2">
      <FlaskConical className="h-4 w-4 shrink-0 text-warning" />
      <span className="text-sm text-fg">{t('embedded.demoLabel')}</span>
      <button
        type="button"
        onClick={() => void openPortal()}
        disabled={isOpening}
        className={cn(
          CONTROL_MIN_HEIGHT.sm,
          'ml-auto inline-flex items-center text-sm font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      >
        {t('embedded.demoPortalLink')}
      </button>
    </div>
  );
}
